import { useReactiveVar } from '@apollo/client';
import AccountContext from 'context/AccountContext';
import { useContext, useEffect, useMemo } from 'react';
import { parkingVehicleLicensePlate } from 'states/common';
import { latestVehicleUsed } from 'states/persistInStorage';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { Vehicle } from 'types/generatedSchemaTypes';
import {
  getLicensePlateForAlwaysSelectVehicle,
  getLicensePlateForNonAlwaysSelectVehicle,
} from 'utils/vehicleUtils';

const findVehicle = (licensePlateText: string, vehicles: Vehicle[]) => {
  return vehicles.find(
    (currentVehicle: Vehicle) =>
      currentVehicle.licensePlate?.plateText === licensePlateText
  );
};

/**
 * Get the license plate to use for parking
 * @param selectedLicensePlate - The license plate selected by the user
 * @param alwaysSelectVehicle - If the user has selected to always use a vehicle
 * @param latestVehicle - The latest vehicle used by the user
 * @param vehicles - The vehicles of the user
 * @returns The preselected license plate to use for parking
 */
const getLicensePlate = (
  selectedLicensePlate: string,
  alwaysSelectVehicle: boolean,
  latestVehicle: string,
  vehicles: Vehicle[]
) => {
  if (selectedLicensePlate) {
    return selectedLicensePlate;
  }
  if (alwaysSelectVehicle) {
    return getLicensePlateForAlwaysSelectVehicle(latestVehicle, vehicles);
  }

  return getLicensePlateForNonAlwaysSelectVehicle(vehicles);
};

const useSelectedParkingVehicle = (
  skipAutomaticSelection = false,
  parkZoneUid?: string,
  vehiclePlateFromState?: string
) => {
  const {
    customer: { vehicles, applicationPreferences },
  } = useContext(AccountContext);
  const { setParkingVehiclePlate } = useParkingStore((state) => state);
  const latestVehicle = useReactiveVar(latestVehicleUsed);
  const selectedLicensePlate = useReactiveVar(parkingVehicleLicensePlate);
  const hasSession = useParkingStore((state) => state.ongoingParkingSession);
  const parkingTab = useParkzoneStore((state) => state.selectedParkingZoneTab);
  const alwaysSelectVehicle =
    (applicationPreferences?.alwaysSelectVehicle && !skipAutomaticSelection) ??
    false;

  const userStartingCharging = hasSession && parkingTab === 'CHARGE';

  const selectedPlate = vehiclePlateFromState ?? selectedLicensePlate;
  useEffect(() => {
    if (parkZoneUid && !userStartingCharging) {
      parkingVehicleLicensePlate('');
      setParkingVehiclePlate('');
    }
  }, [parkZoneUid, userStartingCharging, setParkingVehiclePlate]);

  const vehicle = useMemo(() => {
    const licensePlate = getLicensePlate(
      selectedPlate,
      alwaysSelectVehicle,
      latestVehicle,
      vehicles || []
    );
    return findVehicle(licensePlate, vehicles || []);
  }, [selectedPlate, vehicles, alwaysSelectVehicle, latestVehicle]);
  return vehicle;
};

export default useSelectedParkingVehicle;
